import PageFormWidget from "../../../../Forms/FormWizard/PageFormRenderer/PageFormWidget/PageFormWidget";
import PageWizardFormWrapper from "../../../../Forms/FormWizard/FormWizardFormWrapper";
import { AutomationWizardStepProps } from "../../../AutomationsUtils";
import { FormProvider, useForm } from "react-hook-form";
import PageFormButtons from "../../../../Forms/FormWizard/PageFormRenderer/PageFormButtons/PageFormButtons";
import { useState, useContext, useMemo, useEffect } from "react";
import {
  ticketEndpointContextEnum,
  ticketEndpointTypeEnum,
  TicketEndpointFields,
} from "@SeemplicityDev/ticket-master";
import { useApolloClient } from "@apollo/client";
import { showToast } from "../../../../Forms/Dialogs/Toast/Toast";
import {
  useUserAnalytics,
  ModalContext,
  type ModalContextType,
  showErrorDialog,
} from "@SeemplicityDev/shared";
import ReportFields from "../../../../AppView/DownloadReport/ReportFields/ReportFields";
import "./CustomizationStep.scss";
import useTicketManagers from "../../../../Ticketing/useTicketManagers/useTicketManagers";
import { FieldsUtilsContext } from "../FieldsUtilsProvider";

const CustomizationStep: React.FC<
  AutomationWizardStepProps & {
    ticketingQueueId?: string;
    fieldMappingId?: string;
    templateId?: number;
  }
> = ({
  defaultValues,
  onValidate = () => {},
  isSummary = false,
  ticketingQueueId,
  fieldMappingId,
  templateId,
}) => {
  const methods = useForm({
    mode: "all",
    values: defaultValues,
  });

  const {
    handleSubmit,
    getValues,
    reset: formReset,
    setValue,
    formState,
  } = methods;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showReportField, setShowReportField] = useState<boolean>(false);
  const { getMutationArray, setGetPropsFromField } =
    useContext(FieldsUtilsContext);

  const { openModal, closeModal } = useContext(
    ModalContext
  ) as ModalContextType;
  const client = useApolloClient();
  const { trackEvent } = useUserAnalytics();
  const { getTicketManagerProps } = useTicketManagers({
    context: ticketEndpointContextEnum.RMQ,
    type: ticketEndpointTypeEnum.TICKET,
  });

  // As in findings report: This effect handles the reset occurring from ReportFields component
  // If columns is in defaultValues (hence, the form is being edited), set the saved form value
  // Otherwise, reapply the value set by ReportFields using setValue. More here: https://betterviewcloud.atlassian.net/browse/WAK-25978.
  useEffect(() => {
    if (
      !!defaultValues &&
      !!formState.defaultValues &&
      !!formState.defaultValues.columns
    ) {
      setValue(
        "columns",
        defaultValues.columns || formState.defaultValues.columns
      );
    }
  }, [defaultValues, formState.defaultValues, setValue]);

  const onCreateTicketTemplateSuccess = (templateName: string) => {
    showToast({
      message: (
        <div className={"modal-text"}>
          The <span className={"modal-live-text"}>{templateName}</span> template
          was created successfully.{" "}
        </div>
      ),
    });
    client.refetchQueries({
      include: ["getTicketTemplatesSummary"],
    });
  };

  const isAttachmentSupported = useMemo(() => {
    if (!!getTicketManagerProps) {
      return getTicketManagerProps(defaultValues?.integrationType)
        ?.attachmentSupported;
    }
  }, [getTicketManagerProps, defaultValues?.integrationType]);

  return (
    <FormProvider {...methods}>
      <PageWizardFormWrapper
        handleSubmit={handleSubmit}
        onSubmit={onValidate}
        getValues={getValues}
      >
        <PageFormWidget isSummary={isSummary}>
          <div
            className={`${
              isSummary ? "customization-step-container-summary" : ""
            }`}
          >
            <TicketEndpointFields
              keysObject={defaultValues?.keys}
              fieldMappingId={fieldMappingId}
              onData={() => {
                if (!!defaultValues?.fields) {
                  formReset({ ...getValues(), fields: defaultValues?.fields });
                }
              }}
              onLoading={(loadingStatus: boolean) => {
                setIsLoading(loadingStatus);

                if (!loadingStatus) {
                  setShowReportField(true);
                }
              }}
              ticketProviderId={defaultValues?.ticketProvider}
              onGetProps={setGetPropsFromField}
              context={ticketEndpointContextEnum.RMQ}
              formMethods={methods}
              filters_config_for_dynamic_mapping={{
                filtersid: defaultValues?.filter?.value?.id,
                scopesid: defaultValues?.scope?.value?.id,
              }}
              templateId={templateId}
              showTicketTemplates
              openModal={openModal}
              closeModal={closeModal}
              getFieldsMutationArray={getMutationArray}
              onCreateTicketTemplateSuccess={onCreateTicketTemplateSuccess}
              onCreateTicketTemplateError={(err: any) => {
                showErrorDialog(err);
              }}
              onEventTriggered={trackEvent}
              viewMode={isSummary}
            />
          </div>
          {showReportField && isAttachmentSupported && !isSummary && (
            <div className="ticketing-customization-report-fields-wrapper">
              <div className="ticketing-customization-report-fields-title">
                Attachments
              </div>
              <div className="ticketing-customization-report-fields-description">
                Customize data columns to include in the attached CSV file.
              </div>
              <ReportFields
                label="Select Columns"
                rmqId={ticketingQueueId}
                useLocalStorage={false}
                fetchPolicy={
                  defaultValues?.columns ? "cache-first" : "no-cache"
                }
              />
            </div>
          )}
        </PageFormWidget>
        {!isSummary && (
          <PageFormButtons
            nextButtonDisabled={isLoading}
            data-testid="customization-step-next-button"
          />
        )}
      </PageWizardFormWrapper>
    </FormProvider>
  );
};

export default CustomizationStep;
